import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calculator, FolderOpen, FileText, Package, UserPlus, LogOut, Users, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AmazonDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [pendingUsersCount, setPendingUsersCount] = useState(0);

  const isAmazonAdmin = user?.role === 'amazon_admin' || user?.role === 'admin' || user?.role === 'zkteco_admin';
  
  // Check if user can go back to main dashboard (admin, zkteco, zkteco_admin)
  const canGoBackToMainDashboard = user?.role === 'admin' || user?.role === 'zkteco' || user?.role === 'zkteco_admin';

  useEffect(() => {
    // Fetch pending users count for admins
    if (isAmazonAdmin) {
      const fetchPendingCount = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/amazon/admin/pending-count`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPendingUsersCount(response.data.count || 0);
        } catch (err) {
          console.error('Error fetching pending count:', err);
        }
      };
      fetchPendingCount();
    }
  }, [isAmazonAdmin]);

  const dashboardItems = [
    {
      id: 'hardening-project',
      title: 'Hardening Project',
      subtitle: 'Internal Entrance Control',
      icon: Calculator,
      color: 'from-orange-500 to-amber-600',
      hoverColor: 'hover:from-orange-600 hover:to-amber-700',
      navigate: '/amazon/hardening-project'
    },
    {
      id: 'saved-quotes',
      title: 'Saved Quotes',
      subtitle: 'View all site configurations',
      icon: FolderOpen,
      color: 'from-green-500 to-emerald-600',
      hoverColor: 'hover:from-green-600 hover:to-emerald-700',
      navigate: '/amazon/saved-quotes'
    },
    {
      id: 'products-datasheets',
      title: 'Products and Datasheets',
      subtitle: 'Browse products and documentation',
      icon: Package,
      color: 'from-blue-500 to-cyan-600',
      hoverColor: 'hover:from-blue-600 hover:to-cyan-700',
      navigate: '/amazon/products-datasheets'
    },
    {
      id: 'invite-team',
      title: 'Invite Team',
      subtitle: 'Invite colleagues to Amazon portal',
      icon: UserPlus,
      color: 'from-teal-500 to-cyan-600',
      hoverColor: 'hover:from-teal-600 hover:to-cyan-700',
      navigate: '/amazon/invite-team'
    }
  ];

  // Admin items - only shown to amazon_admin, admin, zkteco_admin
  const adminItems = isAmazonAdmin ? [
    {
      id: 'admin',
      title: 'Admin Panel',
      subtitle: 'Manage Amazon users',
      icon: Users,
      color: 'from-red-500 to-rose-600',
      hoverColor: 'hover:from-red-600 hover:to-rose-700',
      navigate: '/amazon/admin',
      badge: pendingUsersCount > 0 ? pendingUsersCount : null
    }
  ] : [];

  const allItems = [...dashboardItems, ...adminItems];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Top Right Buttons */}
        <div className="flex justify-between items-center mb-4">
          {/* Back to Main Dashboard - only for admin/zkteco/zkteco_admin */}
          {canGoBackToMainDashboard ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-700 hover:text-slate-900 bg-white"
              data-testid="back-to-main-dashboard-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          ) : (
            <div></div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={onLogout}
            className="text-slate-700 hover:text-slate-900 bg-white"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg">
              <span className="text-white text-3xl font-bold">A</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            Amazon Portal
          </h1>
          <p className="text-lg text-slate-600">
            Welcome back, {user?.first_name || 'User'}
          </p>
          {user?.site_postcode && (
            <p className="text-sm text-slate-500 mt-1">
              Site: {user.site_postcode}
            </p>
          )}
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.navigate)}
                className="group relative overflow-hidden text-left"
              >
                <Card className="border-2 border-slate-200 hover:border-orange-400 transition-all duration-300 h-48">
                  <CardContent className="p-0 h-full">
                    <div className={`h-full bg-gradient-to-br ${item.color} ${item.hoverColor} transition-all duration-300 flex flex-col items-center justify-center text-white relative overflow-hidden`}>
                      {/* Badge */}
                      {item.badge && (
                        <div className="absolute top-4 right-4 z-20">
                          <Badge className="bg-red-500 text-white animate-pulse">
                            {item.badge}
                          </Badge>
                        </div>
                      )}
                      
                      {/* Decorative background pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 left-0 w-32 h-32 border-4 border-white rounded-full -translate-x-16 -translate-y-16"></div>
                        <div className="absolute bottom-0 right-0 w-32 h-32 border-4 border-white rounded-full translate-x-16 translate-y-16"></div>
                      </div>
                      
                      {/* Icon */}
                      <div className="relative z-10 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/50">
                          <Icon className="w-8 h-8" />
                        </div>
                      </div>
                      
                      {/* Text */}
                      <div className="relative z-10 text-center px-4">
                        <h3 className="text-xl font-bold">{item.title}</h3>
                        {item.subtitle && (
                          <p className="text-sm text-white/80 mt-1">{item.subtitle}</p>
                        )}
                      </div>
                      
                      {/* Hover effect */}
                      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AmazonDashboard;
